//ProcessFlowGraph.tsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Edge, Node, OnConnect, NodeMouseHandler } from "reactflow"; // Correct the import
import {
    Background,
    Controls,
    MiniMap,
    ReactFlow,
    addEdge,
    useNodesState,
    useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import axios from 'axios';
import CustomNodeComponent from './CustomNodeComponent'; // Correct the import path if necessary
import CustomOutputNodeComponent from './CustomOutputNodeComponent'; // Correct the import path if necessary
import GroupedNode from './GroupedInputs';
import { EnhancedQueryResult, QueryResult, CustomNodeData } from './types';
import { useStore } from './store'; // Import the useStore hook
import { fetchPriceHistory, fetchElectricityPrices, fetchDataFromAPI } from './api';





const convertQueryResultsToGraphElements = (queryResults: EnhancedQueryResult[]): { nodes: Node[]; edges: Edge[] } => {
    let nodes: Node[] = [];
    let edges: Edge[] = [];

    let currentY = 0; // Define currentYI thin
    let outputNodeX = 0; // Define outputNodeX
    let outputNodeCenterY = 0; // Define outputNodeCenterY
    const startY = 100; // Starting Y-position for the first node
    const spacingY = 50; // Vertical spacing between nodes
    const inputNodeX = 100; // X position of input nodes
    const inputNodesMaxWidth = 100; // Adjust based on your actual maximum width of input nodes
    const horizontalSpacing = 400; // Increased spacing between the last input node and the output node
    const nodeHeight = 5; // Define nodeHeight. Adjust this value as needed.



    queryResults.forEach((item, index) => {
        // Parent Node
        const parentNodeId = `parent-${item.StartingFlowID.low}-${item.ProcessID.low}`;
        const parentNode: Node = {
            id: parentNodeId,
            type: 'groupedNode', // Adjust type to match what you use in nodeTypes
            position: { x: 250, y: 150 },
            data: {
                label: `${item.ProcessName} (${item.ProcessID.low})`,
                inputs: item.Inputs,
                outputs: item.OutputsProduct, // Make sure to include 
                startingflowid: item.StartingFlowID.low,
                processid: item.ProcessID.low,
                ProcessDescription: item.ProcessDescription,
                Technology: item.Technology,
                Geography: item.Geography,
            },
        };
        console.log('Creating initial node:', parentNode);
        nodes.push(parentNode);


        // Calculate the x position of the output node
        outputNodeX = Math.max(...nodes.map(node => node.position.x)) + inputNodesMaxWidth + horizontalSpacing;

        // Output Node
        const outputNode: Node = {
            id: `output-${item.ProcessID.low}`,
            type: 'customOutputNode',

            position: { x: outputNodeX + 1200, y: outputNodeCenterY }, // Use the calculated x-position
            data: { ProcessName: item.ProcessName, LocationName: item.LocationName, outputs: item.OutputsProduct, flowID: item.StartingFlowID.low, processID: item.ProcessID.low, title: `Output: ${item.StartingFlowName}, ${item.LocationName}` },
        };
        nodes.push(outputNode);

        const edgeId = `e${parentNodeId}-to-${outputNode.id}`;
        const edge: Edge = {
            id: edgeId,
            source: parentNodeId, // or `${parentNodeId}__outputHandleId` if using specific output handles
            target: outputNode.id,
            animated: true, // Optional: Adds animation to the edge
            // style, type, etc. can be added to customize the appearance and behavior of the edge
        };
        edges.push(edge);

        console.log('Initial edge:', edge);


    });

    return { nodes, edges };
};








const ProcessFlowGraph = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Access global state variables and actions
    const { flowID, optionalProcessID } = useStore(state => ({
        flowID: state.flowID,
        optionalProcessID: state.optionalProcessID
    }));

    const onConnect: OnConnect = useCallback(
        (connection) => {
            // Log the connection details
            console.log('Manually created connection:', connection);

            // Continue to set the edges as before
            setEdges((eds) => addEdge(connection, eds));
        },
        [setEdges]
    );

    const graphContainerRef = useRef(null);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { height } = entry.contentRect;
                console.log(`New height: ${height}`); // Debugging log
                (entry.target as HTMLElement).style.height = `${height}px`;
            }
        });
    
        if (graphContainerRef.current) {
            console.log("Observing for resize..."); // Debugging log
            resizeObserver.observe(graphContainerRef.current);
        } else {
            console.log("graphContainerRef.current is not available"); // Debugging log
        }
    
        return () => {
            if (graphContainerRef.current) {
                resizeObserver.unobserve(graphContainerRef.current);
            }
        };
    }, []);

    const fetchData = async () => {
        // Use flowID and optionalProcessID from the global state
        // const response = await axios.post<EnhancedQueryResult[]>('http://localhost:3001/query', {
        //     flowID,
        //     optionalProcessID,
        // });
        const response = await fetchDataFromAPI(flowID, optionalProcessID) ?? [];
        // Assume convertQueryResultsToGraphElements is a function that transforms API response to nodes and edges
        const { nodes: newNodes, edges: newEdges } = convertQueryResultsToGraphElements(response);
        setNodes(newNodes);
        setEdges(newEdges);
        return response.map(data => ({
            ...data,
            defaultProviderLocationCodes: data.Inputs.map(input => input.DefaultProviderLocationCode).filter(Boolean) // Assuming DefaultProviderLocationCode is the correct property name
        }));
    };

    useEffect(() => {
        const fetchDataAndPriceHistory = async () => {
            const data = await fetchData();
            if (data) {
                // Extract HS codes from both inputs and outputs
                const hsCodes = Array.from(new Set(data.flatMap(d => [
                    ...d.Inputs.map(input => input.Hscode),
                    ...d.OutputsProduct.map(output => output.Hscode)
                ].filter(Boolean)))); // Ensures we only include non-null, non-undefined HS codes

                // Fetch price history with the extracted HS codes
                const priceHistoryResponse = await fetchPriceHistory(hsCodes);

                if (priceHistoryResponse) {
                    Object.entries(priceHistoryResponse).forEach(([cmCode, historyItems]) => {
                        // Assuming setPriceHistory now takes the CMDCODE and the entire array of items
                        useStore.getState().setPriceHistory(cmCode, historyItems);
                    });
                }
            }
        };

        fetchDataAndPriceHistory();
    }, [flowID, optionalProcessID]); // Depend on global state variables


    useEffect(() => {
        const fetchDataAndElectricityPrices = async () => {
            const dataWithLocationCodes = await fetchData();
            if (dataWithLocationCodes.length > 0) {
                // Flatten and deduplicate DefaultProviderLocationCodes from all nodes,
                // and ensure 'GLO' is included in the set of codes to be queried
                const locationCodes = Array.from(new Set(dataWithLocationCodes.flatMap(item => item.defaultProviderLocationCodes)
                    .concat('GLO'))); // Add 'GLO' to the list
    
                // Fetch electricity prices for the extracted location codes, including 'GLO'
                const electricityPricesResponse = await fetchElectricityPrices(locationCodes);
    
                if (electricityPricesResponse) {
                    // Assuming your setElectricityPrices method in the store accepts ISO_COUNTRY2 and the array of records directly
                    Object.entries(electricityPricesResponse).forEach(([ISO_COUNTRY2, records]) => {
                        // Update global state with new electricity prices data
                        useStore.getState().setElectricityPrices(ISO_COUNTRY2, records);
                    });
                }
            }
        };
    
        fetchDataAndElectricityPrices();
    }, [flowID, optionalProcessID]); // Depend on global state variables
    





    // Add a method to update node data
    const updateNodeData = useCallback((nodeId: string, newData: Partial<CustomNodeData>) => {
        setNodes((prevNodes) =>
            prevNodes.map((node) => {
                if (node.id === nodeId) {
                    return { ...node, data: { ...node.data, ...newData } };
                }
                return node;
            })
        );
    }, [setNodes]);




    const handleNodeDoubleClick = useCallback(async (inputFlowID: number, defaultProviderID: number, startingFlowID: number, processID: number) => {
        console.log('Node double-clicked with IDs:', inputFlowID, defaultProviderID, startingFlowID, processID);

        try {
            // const response = await axios.post<QueryResult[]>('http://localhost:3001/query', {
            //     flowID: inputFlowID,
            //     optionalProcessID: defaultProviderID,
            // });
            const response = await fetchDataFromAPI(inputFlowID.toString(), defaultProviderID.toString())
           // const newDataArray = response.data;
           const newDataArray = response ?? [];

            // Extract location codes from newDataArray
            const defaultProviderLocationCodes = Array.from(new Set(newDataArray.flatMap(data =>
                data.Inputs.map(input => input.DefaultProviderLocationCode).filter(Boolean)
            )));

            if (defaultProviderLocationCodes.length > 0) {
                // Fetch electricity prices for the extracted location codes
                const electricityPricesResponse = await fetchElectricityPrices(defaultProviderLocationCodes);
                if (electricityPricesResponse) {
                    Object.entries(electricityPricesResponse).forEach(([ISO_COUNTRY2, records]) => {
                        // Update global state with new electricity prices data
                        useStore.getState().setElectricityPrices(ISO_COUNTRY2, records);
                    });
                }
            }

            // Prepare updates without applying them yet
            let newNodes = [...nodes]; // Clone the current state
            let newEdges = [...edges]; // Clone the current state




            newDataArray.forEach(newData => {
                const hsCodes = Array.from(new Set([
                    ...newData.Inputs.map(input => input.Hscode),
                    ...newData.OutputsProduct.map(output => output.Hscode)
                ].filter(Boolean))); // Filter out null/undefined HS codes

                if (hsCodes.length > 0) {
                    // Fetch price history for the extracted HS codes
                    fetchPriceHistory(hsCodes).then(priceHistoryResponse => {
                        if (priceHistoryResponse) {
                            Object.entries(priceHistoryResponse).forEach(([cmCode, historyItems]) => {
                                // Update global state with new price history data
                                useStore.getState().setPriceHistory(cmCode, historyItems);
                            });
                        }
                    }).catch(error => console.error("Error fetching price history in double-click:", error));
                }
                // Construct IDs for new parent node and edges using newData properties
                const newParentNodeId = `parent-${newData.StartingFlowID.low}-${newData.ProcessID.low}`;
                const newParentNode = {
                    id: newParentNodeId,
                    type: 'groupedNode',
                    position: { x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight }, // Use the calculated position   replace this when get the node state find working { x: newNodeXPosition, y: newNodeYPosition };
                    data: {
                        label: `${newData.ProcessName} (${newData.ProcessID.low})`,
                        inputs: newData.Inputs,
                        outputs: newData.OutputsProduct,
                        startingflowid: newData.StartingFlowID.low,
                        processid: newData.ProcessID.low,
                        ProcessDescription: newData.ProcessDescription,
                        Technology: newData.Technology,
                        Geography: newData.Geography,
                    },
                }; nodes.push(newParentNode);

                const outputHandleId = `output-${newData.ProcessID.low}-${newData.StartingFlowID.low}-${inputFlowID}`; // Example handle ID
                const targetNodeId = `parent-${startingFlowID}-${processID}`; // Targeting the clicked node
                const targetHandleId = `input-${targetNodeId}-${inputFlowID}`; // Adjust based on your handle ID format

                const newEdge = {
                    id: `e-${newParentNodeId}-to-${targetNodeId}`,
                    source: newParentNodeId,
                    sourceHandle: outputHandleId,
                    target: targetNodeId,
                    targetHandle: targetHandleId,
                    animated: true,
                };
                edges.push(newEdge);

                // Inside your callback or method that handles node creation based on double-click
                const newChildNodeId = newParentNodeId; // The ID of the newly created node
                const parentId = targetNodeId; // The ID of the node from which the double-click action was initiated
                const subInputId = targetHandleId; // The specific handle or sub-input ID within the parent node

                // Now, save this relationship in the global state
                useStore.getState().setParentChildRelation(newChildNodeId, parentId, subInputId);


                console.log('Creating new edge:', newEdge);
                console.log('Creating new node:', newParentNode);
                setNodes((prevNodes) => [...prevNodes, newParentNode]);

                setNodes((prev) => [...prev, newParentNode]);
                setEdges((prev) => [...prev, newEdge]);


            });
        } catch (error) {
            console.error("Error fetching new data for node expansion:", error);
        }
    }, [setNodes, setEdges]);


    // When defining nodeTypes, ensure GroupedNode is correctly receiving the handleNodeDoubleClick prop:
    const nodeTypes = useMemo(() => ({
        customInputNode: CustomNodeComponent,
        customOutputNode: CustomOutputNodeComponent,
        groupedNode: (props: any) => <GroupedNode {...props} onInputDoubleClick={handleNodeDoubleClick} />,
    }), [handleNodeDoubleClick]);


    
    return (
        <div ref={graphContainerRef} style={{ width: '100%', flexGrow: 1, minHeight: '100%' }}> {/* Added minHeight for fallback */}
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                fitView
                style={{ width: '100%', height: '100%' }} // Ensure ReactFlow takes the full container size
            >
                <Background />
                <MiniMap />
                <Controls />
            </ReactFlow>
        </div>
    );
};

export default ProcessFlowGraph;
